import { createSelector } from "@reduxjs/toolkit";

import { RootState } from "@/data/store";
import {
  selectAllowLateResubmission,
  selectHasAutoSubmission,
  selectIsAssignment,
  selectIsExam,
  selectIsLiveExam,
  selectIsTimedAssignment,
  selectIsWindowExam,
} from "@/features/assignment";
import {
  selectHasFinal,
  selectIsSubmitting,
  selectSaveError,
  selectSessionLock,
  selectShowSubmitPrompt,
  selectSubmitError,
} from "@/features/authority";
import {
  selectActiveReadingDate,
  selectActiveWritingDate,
  selectExamEndDatePassed,
  selectExamLateSubmissionDatePassed,
  selectExamStartDatePassed,
  selectFinalDatePassed,
  selectTimedWorkNotStarted,
  selectWorkStartDatePassed,
  selectWritingDatePassed,
} from "@/features/timeline";

//////////////////////////////////////////////////////////////////////////////
// Compound selectors across other slices                                   //
//////////////////////////////////////////////////////////////////////////////

/**
 * Selector for checking Cadmus submission rules for exams and assignments.
 */
export const selectCanSubmit = (state: RootState) => {
  // Cannot request submissions in error or locked states.
  if (selectSessionLock(state) || selectSaveError(state)) {
    return false;
  }

  // Conditions for Exams
  if (selectIsExam(state)) {
    // Cannot submit before the exam is open
    if (!selectExamStartDatePassed(state)) {
      return false;
    }

    if (selectTimedWorkNotStarted(state)) {
      return false;
    }

    // Cannot request submissions during reading time.
    if (selectActiveReadingDate(state) !== null) {
      return false;
    }

    // Cannot submit after the writing/end date IF you have a submission
    if (selectWritingDatePassed(state) && !selectCanSubmitLateExam(state)) {
      return false;
    }
  }

  // Conditions for Timed assignments
  if (selectIsTimedAssignment(state)) {
    // Cannot submit before the work start date
    if (!selectWorkStartDatePassed(state)) {
      return false;
    }

    // No late re-submissions if not allowed
    if (
      selectWritingDatePassed(state) &&
      !selectCanSubmitLateAssignment(state)
    ) {
      return false;
    }
  }

  // Conditions for assignments
  if (selectFinalDatePassed(state) && !selectCanSubmitLateAssignment(state)) {
    return false;
  }

  return true;
};

/**
 * Select if late exam submission can be made.
 */
export const selectCanSubmitLateExam = createSelector(
  selectIsExam,
  selectHasAutoSubmission,
  selectExamLateSubmissionDatePassed,
  selectHasFinal,
  (isExam, autoSubmission, lateSubmissionDatePassed, hasFinal) => {
    return isExam && !autoSubmission && !lateSubmissionDatePassed && !hasFinal;
  }
);

/**
 * Select if a late assignment submission can be made.
 */
export const selectCanSubmitLateAssignment = createSelector(
  selectIsAssignment,
  selectAllowLateResubmission,
  selectHasFinal,
  (isAssn, allowLateResubmission, hasFinal) => {
    if (!isAssn) return false;
    if (hasFinal && allowLateResubmission) return true;
    if (hasFinal) return false;
    return true;
  }
);

/**
 * Select if the student can continue editing.
 */
export const selectCanUnsubmit = (state: RootState) => {
  if (selectIsLiveExam(state)) {
    if (selectWritingDatePassed(state)) {
      return false;
    }
  }

  if (selectIsWindowExam(state)) {
    if (selectWritingDatePassed(state)) {
      return false;
    }
  }

  if (selectIsTimedAssignment(state)) {
    if (selectWritingDatePassed(state) || selectFinalDatePassed(state)) {
      if (!selectCanSubmitLateAssignment(state)) {
        return false;
      }
    }
  }

  if (selectFinalDatePassed(state) && !selectCanSubmitLateAssignment(state)) {
    return false;
  }

  return true;
};

/**
 * Select if the student can currently edit their work editors.
 */
export const selectCanEdit = (state: RootState) => {
  if (
    selectSessionLock(state) ||
    selectShowSubmitPrompt(state) ||
    selectSubmitError(state) ||
    selectIsSubmitting(state)
  ) {
    return false;
  }

  if (selectIsExam(state)) {
    if (selectTimedWorkNotStarted(state)) {
      return false;
    }

    if (selectActiveReadingDate(state) !== null) {
      return false;
    }

    if (selectActiveWritingDate(state) !== null) {
      return true;
    }

    if (selectCanSubmitLateExam(state)) {
      return true;
    }

    return false;
  }

  if (selectIsTimedAssignment(state)) {
    if (!selectWorkStartDatePassed(state)) {
      return false;
    }

    if (
      selectWritingDatePassed(state) &&
      !selectCanSubmitLateAssignment(state)
    ) {
      return false;
    }
  }

  return true;
};

/**
 * Compute the stage at which the exam is at currently.
 *
 * Both timed and windowed exam can be in one of the following stages:
 *
 *   1. `scheduled`: The exam is scheduled but not yet started.
 *   2. `locked`: The exam is locked and needs student ack to start. (live exams
 *      skip this stage automatically)
 *   3. `open`: The exam is open and can be started.
 *   4. `closed`: The exam is closed and cannot be started.
 *
 */
export const selectExamStage = createSelector(
  selectIsWindowExam,
  selectIsLiveExam,
  selectTimedWorkNotStarted,
  selectExamStartDatePassed,
  selectExamEndDatePassed,
  (
    isWindowExam,
    isLiveExam,
    timedWorkNotStarted,
    examStartDatePassed,
    examEndDatePassed
  ) => {
    // Flags to control sheet panel rendering
    let examStage: "scheduled" | "locked" | "open" | "closed" = "scheduled";

    if (isWindowExam && timedWorkNotStarted) {
      if (!examStartDatePassed || examEndDatePassed) {
        examStage = "scheduled";
      } else {
        examStage = "locked";
      }
    } else if (isLiveExam && !examStartDatePassed) {
      examStage = "scheduled";
    } else if (isLiveExam && examEndDatePassed) {
      examStage = "closed";
    } else {
      examStage = "open";
    }

    return examStage;
  }
);
